import React, { useState } from 'react'
import fetch from '~/common/fetch'
import FileUploadZone from '~/components/FileUploadZone'
import { ItemsListItem, ItemsListItems } from '~/components/v2/ItemsList'
import {
  Box,
  Button,
  ButtonGroupActions,
  IconButton,
  Link,
  List,
  ListItem,
  MenuItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalDialog,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
  useDisclosure,
  useErrorModal,
  useSuccessToast
} from '~/design_system'
import { Transfer, Trash } from '~/icons'
import { useWaitAndDownload } from '~/reports/table/helpers'
import { textStyles } from '~/theme'
import { REPORTS_HOME_PATH } from '~/custom_reports/constants'
import { Msg, useMsg } from '~/common/localizationUtils'

export const VsyncReportImport = ({ isOpen, onClose }) => {
  /*
   * Import report that was exported via Vsync
   * Pass the file to the API end-point to process.
   */
  const msg = useMsg()
  const [isLoading, setIsLoading] = useState(false)
  const MAX_FILE_SIZE = 10485760
  const [importFile, setImportFile] = useState(null)
  const { onToggle: isSuccessToggle } = useDisclosure()
  const successToast = useSuccessToast()
  const { showModal: showErrorModal } = useErrorModal()
  const onClickImportReport = () => {
    // Initiate the call to API to start it as a task here and check periodically
    setIsLoading(true)
    const url = '/api/custom_reports/vsync-report/import/'
    const reportUrl = (report_id, report_type) => {
      return `${REPORTS_HOME_PATH}/${report_type}/${report_id}`
    }

    const fd = new FormData()
    fd.append('file', importFile)

    fetch(url, {
      method: 'POST',
      noContentType: true,
      body: fd
    })
      .then(response => response.json())
      .then(response => {
        setImportFile(null)
        setIsLoading(false)
        onClose()
        if (Object.prototype.hasOwnProperty.call(response, 'error')) {
          showErrorModal({ title: 'Error', message: response.error })
          return
        }

        isSuccessToggle()
        successToast(
          <Box>
            <Box>Following reports imported:</Box>
            <Spacer />
            <List spacing={3}>
              {response.imported_reports.map((report, index) => (
                <ListItem key={`${report.report_id}-${report.report_type}`}>
                  <Link
                    variant="unstyled"
                    href={reportUrl(report.report_id, report.report_type)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`imported-report-link-${index}`}>
                    {report.name}
                  </Link>
                </ListItem>
              ))}
            </List>
          </Box>
        )
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .catch(err => {
        setIsLoading(false)
        setImportFile(null)
        showErrorModal({ message: 'Failed importing from file.' })
      })
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Msg id="common.ui.upload_term" term={msg('custom_report.report_definition')} />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={6}>
            <Stack spacing={6}>
              <Box>
                <FileUploadZone
                  maxSize={MAX_FILE_SIZE}
                  onFileDrop={file => setImportFile(file)}
                  acceptedFormats=".json, application/json"
                  supportedFormatText="JSON"
                  maxSizeText="10 MB"
                  isLoading={isLoading}
                />
              </Box>
              <ItemsListItems maxHeight="300px" overflow="auto">
                {importFile && (
                  <ItemsListItem key={importFile.name} justifyContent="space-between" p={2}>
                    <Text {...textStyles.body.md.default}>{importFile.name}</Text>
                    <IconButton
                      aria-label="Remove file"
                      icon={<Trash />}
                      variant="ghost"
                      size="sm"
                      onClick={() => setImportFile(null)}
                    />
                  </ItemsListItem>
                )}
              </ItemsListItems>
              <ButtonGroupActions>
                <Button
                  data-testid="import-vsync-report-action-json-button"
                  isLoading={isLoading}
                  onClick={onClickImportReport}
                  variant="primary"
                  disabled={!importFile}>
                  <Msg id="common.ui.import" />
                </Button>
                <Button
                  data-testid="cancel-import-vsync-report-action-json-button"
                  onClick={() => {
                    setImportFile(null)
                    onClose()
                  }}>
                  Cancel
                </Button>
              </ButtonGroupActions>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export const VsyncReportExportButton = ({ reports, buttonStyle = 'menuItem', ...props }) => {
  /*
   *
   * Export report for transferring it between different environment for the same tenant
   * Such as: export given report >>  {tenant}-test.vndly.com -> {tenant}.vndly.com
   *
   * */

  const msg = useMsg()
  const isExportDisabled = reports.length === 0
  const [isLoading, setIsLoading] = useState(false)
  const { showModal: showErrorModal } = useErrorModal()
  const { isOpen, onToggle, onClose } = useDisclosure()

  const waitAndDownload = useWaitAndDownload()
  const onClickExportReport = () => {
    onClose()
    // Initiate the call to API to start it as a task here and check periodically
    setIsLoading(true)
    const url = '/api/custom_reports/vsync-report/export/'
    const data = { report_ids: reports.map(i => i.id) }
    // This needs to be a modular function
    const getCookie = name => {
      const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))
      if (match) return match[2]
      return null
    }
    fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCookie('csrftoken')
      }
    })
      .then(r => r.json())
      .then(response => {
        if (response.detail) {
          showErrorModal({ title: 'Error', message: response.detail })
          setIsLoading(false)
          return
        }
        waitAndDownload(response.task_id, {
          title: msg('common.ui.downloaded_term', { term: msg('custom_report.report_definition') }),
          onTaskSuccess: () => setIsLoading(false)
        })
      })
      .catch(err => {
        setIsLoading(false)
        showErrorModal({ error: err, message: 'Something went wrong!\n\r Please try again later' })
      })
  }

  const isMenuItem = buttonStyle === 'menuItem'
  const ComponentElement = isMenuItem ? MenuItem : Button
  const reportCount = reports?.length ?? 0
  return (
    <>
      <ComponentElement
        onClick={onToggle}
        icon={<Transfer color="var(--vndly-color-container-icon)" />}
        isDisabled={isExportDisabled}
        {...(isMenuItem && { isLoading })}
        {...props}>
        <Msg id="common.ui.export_term" term={msg('custom_report.report_definition')} />
      </ComponentElement>

      <ModalDialog isOpen={isOpen} onClose={onClose}>
        <ModalDialogHeader>
          <Msg id="custom_report.confirm.download_selected_report_definition.title" count={reportCount} />
        </ModalDialogHeader>
        <ModalDialogBody>Are you sure you want to proceed?</ModalDialogBody>
        <ModalDialogFooter>
          <ButtonGroupActions>
            <Button
              data-testid="download-vsync-report-action-json-button"
              variant="primary"
              onClick={onClickExportReport}>
              <Msg id="common.ui.download" count={1} />
            </Button>
            <Button data-testid="export-vsync-report-action-json-button" onClick={onClose}>
              <Msg id="common.ui.cancel" />
            </Button>
          </ButtonGroupActions>
        </ModalDialogFooter>
      </ModalDialog>
    </>
  )
}
